import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import { videoData, categories, stats, faqs } from '@/mocks/data';

function HeroSection() {
  const [searchFocused, setSearchFocused] = useState(false);

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20composition%20with%20flowing%20gradient%20waves%20in%20warm%20coral%20orange%20and%20deep%20teal%20green%20tones%2C%20smooth%20fluid%20shapes%2C%20dreamy%20atmospheric%20effect%2C%20cinematic%20wide%20landscape%2C%20minimalist%20and%20elegant%2C%20soft%20diffused%20lighting%2C%20luxury%20dark%20aesthetic%20with%20warm%20highlights&width=1800&height=1000&seq=hero-bg-main&orientation=landscape"
          alt="黄瓜视频网页版 - 免费在线视频平台"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 md:mb-6 leading-tight">
            免费在线观看
            <br className="sm:hidden" />
            <span className="text-primary-400"> 无需下载</span>
          </h1>
          <p className="text-base md:text-lg text-white/80 mb-8 md:mb-10 max-w-xl mx-auto leading-relaxed">
            黄瓜视频网页版，直接在浏览器打开即可畅享海量视频内容。
            支持PC端与移动端，操作简单流畅，随时随地享受优质视频体验。
          </p>

          <div
            className={`flex items-center max-w-lg mx-auto rounded-full overflow-hidden transition-all duration-300 ${
              searchFocused ? 'ring-2 ring-primary-400/60' : ''
            }`}
          >
            <div className="flex-1 bg-white/95 backdrop-blur-sm flex items-center px-5 py-3.5 md:py-4">
              <i className="ri-search-line text-foreground-400 text-lg mr-3"></i>
              <input
                type="text"
                placeholder="搜索你喜欢的电影、电视剧、综艺..."
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </div>
            <button className="bg-primary-500 hover:bg-primary-600 text-background-50 px-6 md:px-8 py-3.5 md:py-4 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer">
              搜索
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            {categories.slice(0, 4).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/85 text-xs md:text-sm hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/category/dianying"
              className="px-4 py-1.5 rounded-full text-primary-300 text-xs md:text-sm hover:text-primary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              视频分类
            </h2>
            <p className="text-sm text-foreground-500 mt-1">探索你感兴趣的精彩内容</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all mb-3">
                <i className={`${cat.icon} text-xl md:text-2xl`}></i>
              </div>
              <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                {cat.name}
              </span>
              <span className="text-xs text-foreground-400 mt-1">{cat.slug === 'dianying' ? '2.3万部' : cat.slug === 'dianshiju' ? '1.8万部' : cat.slug === 'zongyi' ? '8500期' : cat.slug === 'dongman' ? '1.2万集' : cat.slug === 'jilupian' ? '6200部' : '9800场'}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoCard({ video }: { video: typeof videoData[0] }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute bottom-2 right-2 bg-black/75 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-md">
          {video.duration}
        </div>
        <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md">
          {video.rating}
        </div>
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {video.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
          {video.description}
        </p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-play-circle-line"></i>
            在线观看
          </span>
        </div>
      </div>
    </Link>
  );
}

function FeaturedSection() {
  const featured = useMemo(() => videoData.slice(0, 6), []);

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              精选推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">为你精选的高分热门视频</p>
          </div>
          <Link
            to="/category/dianying"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const latest = useMemo(() => videoData.slice(6, 12), []);

  return (
    <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              最新上线
            </h2>
            <p className="text-sm text-foreground-500 mt-1">刚刚更新的精彩内容</p>
          </div>
          <Link
            to="/category/dianshiju"
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看更多 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {latest.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-accent-500">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">
            千万用户的选择
          </h2>
          <p className="text-sm text-background-50/80 mt-2">数据见证我们的品质与信赖</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-50/15 mb-4">
                <i className={`${stat.icon} text-2xl md:text-3xl text-background-50`}></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                {stat.value}
              </div>
              <div className="text-sm text-background-50/75 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 mt-1">快速了解黄瓜视频网页版</p>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70">
              <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                <span className="text-sm md:text-base font-semibold text-foreground-800 pr-4">
                  {faq.question}
                </span>
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-100 flex-shrink-0">
                  <i className="ri-add-line text-sm text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                </div>
              </summary>
              <div className="px-5 pb-5">
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20waves%20in%20coral%20and%20amber%20tones%2C%20smooth%20elegant%20shapes%2C%20cinematic%20atmosphere%2C%20inviting%20and%20warm%20feeling%2C%20minimalist%20abstract%20composition&width=1800&height=600&seq=cta-bg-main&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-foreground-950/80"></div>
      </div>
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
          即刻开始你的观影之旅
        </h2>
        <p className="text-sm md:text-base text-background-50/70 mb-8 max-w-lg mx-auto leading-relaxed">
          无需下载，无需注册，打开浏览器就能观看海量视频。随时随地，畅享精彩内容。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/dianying"
            className="px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            立即观看
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 rounded-full bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黄瓜视频网页版',
    url: 'https://www.huanggua-video.com',
    description: '黄瓜视频网页版是一款无需下载安装、直接在浏览器打开即可使用的在线视频平台。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.huanggua-video.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title="黄瓜视频网页版 - 免费在线观看，无需下载"
        description="黄瓜视频网页版是一款无需下载安装、直接在浏览器打开即可使用的在线视频平台。支持PC端与移动端访问，海量视频内容免费观看，操作简单流畅，随时随地享受优质视频体验。"
        keywords="黄瓜视频网页版,黄瓜视频在线观看,黄瓜视频网页入口,黄瓜视频PC版,黄瓜视频免费观看,黄瓜视频官网网页,在线视频网页版"
        canonicalUrl="https://www.huanggua-video.com"
        schema={homeSchema}
      />

      <main>
        <HeroSection />
        <CategorySection />
        <FeaturedSection />
        <StatsSection />
        <LatestSection />
        <FAQSection />
        <CTASection />
      </main>
    </>
  );
}